/* ─── Array rendering for MCP Analytics (MS-701 AC10b, MS-702 AC4/AC7) ───

   Two renderings of one list, and both live here so they cannot drift:

   * `inlineArray`   — the one-line form used by BOTH the Description column on
     Tool Invocations and the Search input column on Search Activity. Those two
     cells show the same kind of value and must show it identically; giving each
     its own formatter is exactly how they stop matching.
   * `jsonArrayBlock` — the multi-line form used only by the details drawer.

   Batch entries are always ordered alphabetically (`sortedForMode`), so the same
   batch reads the same way every time regardless of the order the client happened
   to send them in. Prototype: `anInlineArray` / `anJsonArray` / `anDescItems`. */

export type InvocationMode = 'single' | 'batch' | string

/** Alphabetical for a batch; untouched for a single call. Never mutates the input. */
export function sortedForMode(values: readonly string[], mode: InvocationMode): string[] {
  const present = values.filter(Boolean)
  return mode === 'batch' ? [...present].sort((a, b) => a.localeCompare(b)) : present
}

/**
 * `[a, b]`, or `["a", "b"]` when quoted.
 *
 * An empty list renders as the empty string rather than `[]`: the cell falls back
 * to an em dash, and a literal `[]` would read as "an empty result" instead of
 * "nothing to show". The drawer's block form does the opposite, deliberately —
 * that field is labelled, so an explicit empty array is the honest answer there.
 */
export function inlineArray(values: readonly string[], options?: { quoted?: boolean }): string {
  const present = values.filter(Boolean)
  if (present.length === 0) return ''
  const parts = options?.quoted ? present.map(v => `"${v}"`) : present
  return `[${parts.join(', ')}]`
}

/**
 * A plain JSON array, one value per line — no wrapper object and no attribute
 * name, so the block shows the values and nothing else.
 */
export function jsonArrayBlock(values: readonly string[]): string {
  const present = values.filter(Boolean)
  if (present.length === 0) return '[]'
  return `[\n${present.map(v => `  "${v}"`).join(',\n')}\n]`
}
